/**
 * Manages the theme for the entire application (site and game).
 * This manager is responsible for loading the correct theme stylesheet,
 * handling theme switching, and persisting the user's choice.
 *
 * Supports per-context themes:
 * - 'dashboard': Theme for the dashboard/game board
 * - '{gameId}': Theme for a specific game
 */

// Configurable variable for available themes and the default theme
const config = {
    themes: ['beach', 'sunset', 'night', 'sunrise', 'mountains', 'forest', 'pride', 'independence', 'halloween', 'christmas', 'mexico', 'egypt', 'japan', 'ancient'], // All themes available in the application
    defaultTheme: 'beach',     // The theme for users who haven't selected one
    themePath: '/themes/',
    stylesheetId: 'dynamic-theme-style',
    storageKeyPrefix: 'kridacards-theme-', // Will be followed by context (dashboard or gameId)
    timestampKeyPrefix: 'kridacards-theme-timestamp-' // Track when theme was last changed locally per context
};

let currentTheme = null;
let currentContext = 'dashboard'; // 'dashboard' or a gameId
let unlockedThemes = null; // Cache for user's themes
let cachedPlayerThemes = null; // Cache the full themes object from Firestore
const THEME_CHANGE_GRACE_PERIOD_MS = 5000; // Don't overwrite localStorage within 5 seconds of a local change

import { getPlayerDocument, getCurrentUser, getFirestoreService } from './krida-auth.js';
import { doc, updateDoc } from "https://www.gstatic.com/firebasejs/10.12.2/firebase-firestore.js";

/**
 * Gets the localStorage key for a given context
 */
function getStorageKey(context) {
    return `${config.storageKeyPrefix}${context || currentContext}`;
}

/**
 * Gets the timestamp localStorage key for a given context
 */
function getTimestampKey(context) {
    return `${config.timestampKeyPrefix}${context || currentContext}`;
}

/**
 * Loads a specific theme by creating or updating a <link> element in the <head>.
 * Since all theme CSS files are preloaded by theme-loader.js, this switch is instant
 * (the CSS is already in the browser cache). Images load lazily when the CSS is applied.
 * @param {string} themeName - The name of the theme to load.
 */
function loadTheme(themeName) {
    if (!themeName || !config.themes.includes(themeName)) {
        console.warn(`Theme "${themeName}" is invalid. Loading default.`);
        themeName = config.defaultTheme;
    }

    // Remove the old stylesheet to ensure a clean load
    const existingLink = document.getElementById(config.stylesheetId);
    if (existingLink) {
        existingLink.remove();
    }

    const themeUrl = `${config.themePath}${themeName}/theme.css`;

    // Create new stylesheet link - CSS loads instantly from preload cache
    const newLink = document.createElement('link');
    newLink.id = config.stylesheetId;
    newLink.rel = 'stylesheet';
    newLink.href = themeUrl;
    newLink.dataset.theme = themeName;

    // After the new stylesheet loads, update everything that depends on it.
    newLink.onload = () => {
        document.body.setAttribute('data-theme', themeName);
        currentTheme = themeName;
        updateFavicon();
        updateNewGameModalBackground();
        detectThemeAssets(themeName);
    };

    newLink.onerror = () => {
        console.error(`Failed to load theme stylesheet: ${themeUrl}`);
    };

    document.head.appendChild(newLink);
}

function updateNewGameModalBackground() {
    setTimeout(() => {
        const modalBgImage = getComputedStyle(document.documentElement).getPropertyValue('--new-game-modal-bg-image').trim();
        const newGameModalContent = document.querySelector('#new-game-modal .modal-content');

        if (newGameModalContent && modalBgImage) {
            newGameModalContent.style.backgroundImage = modalBgImage;
        }
    }, 100); // Timeout to allow the new stylesheet to be parsed
}

/**
 * Auto-detects convention-based theme assets by checking known paths.
 * Drop an image at the right path and it just works — no CSS edits needed.
 *
 * Convention:
 *   /themes/{name}/images/background.jpg  → page background
 *   /themes/{name}/images/card-back.png   → card back design
 *
 * Only overrides if the theme's CSS hasn't already set the variable to something
 * other than 'none'. This way, CSS-defined assets take priority.
 */
function detectThemeAssets(themeName) {
    const basePath = `${config.themePath}${themeName}/images`;

    // Background image: check jpg, webp, png
    const bgExtensions = ['jpg', 'webp', 'png'];
    detectAsset(bgExtensions.map(ext => `${basePath}/background.${ext}`), (url) => {
        // Only override if CSS hasn't set a specific background
        const current = getComputedStyle(document.documentElement)
            .getPropertyValue('--page-background-image').trim();
        if (!current || current === 'none') {
            document.documentElement.style.setProperty(
                '--page-background-image', `url('${url}')`
            );
        }
    });

    // Card back: check png, webp, jpg
    const cardExtensions = ['png', 'webp', 'jpg'];
    detectAsset(cardExtensions.map(ext => `${basePath}/card-back.${ext}`), (url) => {
        const current = getComputedStyle(document.documentElement)
            .getPropertyValue('--card-back-image').trim();
        if (!current || current === 'none') {
            document.documentElement.style.setProperty(
                '--card-back-image', `url('${url}')`
            );
        }
    });
}

/**
 * Tries URLs in order, calls callback with the first one that exists.
 */
function detectAsset(urls, callback) {
    let index = 0;
    function tryNext() {
        if (index >= urls.length) return;
        const url = urls[index++];
        const img = new Image();
        img.onload = () => callback(url);
        img.onerror = tryNext;
        img.src = url;
    }
    tryNext();
}

/**
 * Updates the site's favicon based on the current theme's CSS variable.
 */
function updateFavicon() {
    setTimeout(() => {
        const faviconVar = getComputedStyle(document.documentElement).getPropertyValue('--favicon-url').trim();
        if (faviconVar) {
            const faviconUrl = faviconVar.replace(/url\(['"]?(.*?)['"]?\)/, '$1');
            const faviconLink = document.querySelector("link[rel*='icon']");
            if (faviconLink && faviconUrl) {
                faviconLink.href = faviconUrl;
            }
        }
    }, 100);
}

/**
 * Initializes the theme manager on page load.
 * @param {string} context - The context: 'dashboard' or a gameId
 */
function init(context = 'dashboard') {
    currentContext = context;
    // Load from localStorage for this specific context
    const storageKey = getStorageKey(context);
    currentTheme = localStorage.getItem(storageKey) || config.defaultTheme;

    // Always call loadTheme to ensure theme is applied
    // This acts as a fallback in case theme-loader.js had issues
    loadTheme(currentTheme);
}

/**
 * Sets the current context (dashboard or gameId) and loads the appropriate theme.
 * @param {string} context - The context: 'dashboard' or a gameId
 */
function setContext(context) {
    if (currentContext === context) {
        return;
    }

    currentContext = context;

    // Load the theme for this context from localStorage first (instant)
    const storageKey = getStorageKey(context);
    const savedTheme = localStorage.getItem(storageKey) || config.defaultTheme;
    loadTheme(savedTheme);
}

/**
 * Gets the theme for a specific context from the cached player themes.
 * @param {string} context - The context to get theme for
 * @returns {string|null} The theme name or null if not set
 */
function getThemeForContext(context) {
    if (!cachedPlayerThemes) return null;

    if (context === 'dashboard') {
        return cachedPlayerThemes.dashboard || cachedPlayerThemes.active || null;
    } else {
        // It's a gameId
        return cachedPlayerThemes.games?.[context] || null;
    }
}

/**
 * Fetches theme data from Firestore and updates the UI.
 * This should be called after a user is authenticated.
 * @param {object|null} playerDoc - Optional. A pre-fetched player document to use instead of re-fetching.
 */
export async function syncThemesFromFirestore(playerDoc = null) {
    // If a player document isn't passed in, fetch it.
    if (!playerDoc) {
        playerDoc = await getPlayerDocument();
    }

    if (playerDoc && playerDoc.themes) {
        // Cache the unlocked themes
        if (playerDoc.themes.unlocked) {
            unlockedThemes = playerDoc.themes.unlocked;
        }

        // Cache the full themes object
        cachedPlayerThemes = playerDoc.themes;

        // Check if a local theme change happened recently (within grace period)
        const timestampKey = getTimestampKey(currentContext);
        const lastLocalChange = parseInt(localStorage.getItem(timestampKey) || '0', 10);
        const timeSinceChange = Date.now() - lastLocalChange;
        const isRecentLocalChange = timeSinceChange < THEME_CHANGE_GRACE_PERIOD_MS;

        // Get the theme for the current context from Firestore
        let firestoreTheme = getThemeForContext(currentContext);

        // Fallback: if no context-specific theme, use the global active theme
        if (!firestoreTheme && playerDoc.themes.active) {
            firestoreTheme = playerDoc.themes.active;
        }

        if (firestoreTheme) {
            const localTheme = getCurrentTheme();

            if (firestoreTheme !== localTheme) {
                if (!isRecentLocalChange) {
                    // No recent local change - Firestore is authoritative
                    loadTheme(firestoreTheme);
                    localStorage.setItem(getStorageKey(currentContext), firestoreTheme);
                }
            }
        }
    } else {
        // This case handles a logged-in user who doesn't have theme data yet.
        unlockedThemes = ['beach']; // Give them defaults
        setTheme(getCurrentTheme());
    }
}

/**
 * Public method to change the theme and save the choice for the current context.
 * Persists the theme to both localStorage and Firestore.
 * @param {string} themeName - The theme to switch to.
 */
async function setTheme(themeName) {
    const available = getAvailableThemes();
    if (!available.includes(themeName)) {
        console.warn(`ThemeManager: Theme "${themeName}" is locked. Switch rejected.`);
        return;
    }

    loadTheme(themeName);

    // Save to localStorage with timestamp for this context
    localStorage.setItem(getStorageKey(currentContext), themeName);
    localStorage.setItem(getTimestampKey(currentContext), Date.now().toString());

    // Use getCurrentUser() to get the authenticated user with uid
    const user = getCurrentUser();
    if (user && user.uid) {
        try {
            const db = getFirestoreService();
            if (!db) {
                console.error('ThemeManager.setTheme: Firestore service not available');
                return;
            }
            const playerRef = doc(db, "players", user.uid);

            // Build the update object based on context
            let updateData;
            if (currentContext === 'dashboard') {
                updateData = { "themes.dashboard": themeName };
            } else {
                // It's a gameId - store under themes.games.{gameId}
                updateData = { [`themes.games.${currentContext}`]: themeName };
            }

            await updateDoc(playerRef, updateData);
        } catch (error) {
            console.error("ThemeManager: Error saving theme to Firestore:", error);
        }
    }

    document.dispatchEvent(new CustomEvent('theme-changed', {
        detail: { themeName: getCurrentTheme(), context: currentContext }
    }));
}

function getAvailableThemes() {
    // Return the cached themes if available, otherwise the fallback.
    return unlockedThemes || ['beach'];
}

function getAllThemes() {
    return config.themes;
}

function getCurrentTheme() {
    return currentTheme || localStorage.getItem(getStorageKey(currentContext)) || config.defaultTheme;
}

function getCurrentContext() {
    return currentContext;
}

// Expose public methods
export const ThemeManager = {
    init,
    setContext,
    setTheme,
    getAvailableThemes,
    getAllThemes,
    getCurrentTheme,
    getCurrentContext,
    loadTheme,
    syncThemesFromFirestore
};
